import styled from '@emotion/styled';
import { Text, Tooltip } from '@innovationdepartment/proxima-ui';
import type React from 'react';

const CreativeStrategyInsightsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px 40px;
  margin-left: 60px;
  margin-right: 60px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;

  h2 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 1.8em;
    font-weight: 700;
  }

  .subtitle {
    margin: 0 0 30px 0;
    color: #777;
    font-size: 1em;
    line-height: 1.4;
  }

  .insights-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    width: 100%;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }
`;

const InsightCard = styled.div`
  width: 100%;

  h3 {
    margin: 0 0 20px 0;
    color: #555;
    font-size: 1.1em;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .insight-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .insight-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    font-size: 0.9em;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .insight-label {
    color: #333;
    font-weight: 500;
  }

  .insight-percentage {
    color: #666;
    font-weight: 600;
    margin-left: auto;
  }
`;

interface SpecializedIndividualTag {
  display_name: string;
  percentage_of_buyers: number;
}

interface SpecializedTagType {
  type_display_name: string;
  type_helper_text: string;
  individual_tags: SpecializedIndividualTag[];
}

interface OtherBrandsAnalysis {
  specialized_tag_types?: SpecializedTagType[];
}

interface CreativeStrategyInsightsProps {
  brandPersonasData: {
    personasData: {
      personas: unknown[];
      overall_population: unknown;
      other_brands_analysis: unknown;
    };
    other_brands_analysis: {
      specialized_tag_types: unknown[];
    };
    crossBrandPurchases: unknown[];
  };
}

const CreativeStrategyInsights: React.FC<CreativeStrategyInsightsProps> = ({
  brandPersonasData
}) => {
  const otherBrandsAnalysis = brandPersonasData?.personasData?.other_brands_analysis as
    | OtherBrandsAnalysis
    | undefined;

  const renderInsightCard = (tagType: SpecializedTagType) => (
    <InsightCard>
      <h3>
        {tagType.type_display_name}
        <Tooltip
          title={
            <div style={{ padding: '12px 7px', width: 'center' }}>
              <Text variant="body3">{tagType.type_helper_text}</Text>
            </div>
          }
          placement="top"
          arrow
          info
        />
      </h3>
      <ul className="insight-list">
        {tagType.individual_tags.map(item => (
          <li key={`${tagType.type_display_name}-${item.display_name}`} className="insight-item">
            <span className="insight-label">{item.display_name}</span>
            <span className="insight-percentage">{item.percentage_of_buyers.toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </InsightCard>
  );

  return (
    <CreativeStrategyInsightsSection>
      <h2>Creative Strategy Insights</h2>
      <p className="subtitle">
        Your customers&apos; brand type shopping activity across other brands.
      </p>

      <div className="insights-grid">
        {/* Left Column */}
        {otherBrandsAnalysis?.specialized_tag_types?.map(tagType => (
          <div key={tagType.type_display_name}>{renderInsightCard(tagType)}</div>
        ))}
      </div>
    </CreativeStrategyInsightsSection>
  );
};

export default CreativeStrategyInsights;
